/**
 * Cohort detail's ATTENDANCE tab (F3-A): per-session roll call
 * (present/late/absent/excused via write-path), check-in code screen
 * (large display, open/close via REST), stats column and risk alerts
 * panel with acknowledge.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { BellRing, KeyRound } from "@fleet/icons";
import {
  attachIdentity,
  type AttendanceStatus,
  handleApiResponse,
  setAttendanceMutation,
  sortByIdentityName,
} from "@space/shared";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { ExportButton } from "@/features/io/components/export-button";
import { useCohortRoster } from "../hooks/use-cohort-data";
import { useCohortIdentities } from "../hooks/use-org-identities";
import {
  useCohortAlerts,
  useCohortAttendanceReport,
  useCohortSessions,
  useSessionAttendance,
} from "../hooks/use-schedule-data";

const STATUSES: { value: AttendanceStatus; label: MessageDescriptor }[] = [
  { value: "present", label: msg`Present` },
  { value: "late", label: msg`Late` },
  { value: "absent", label: msg`Absent` },
  { value: "excused", label: msg`Excused` },
];

export const CohortAttendanceTab: Component<{
  cohortId: string;
  organizationId: string;
  campusId: string | null;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const sessions = useCohortSessions(() => props.cohortId);
  const enrollments = useCohortRoster(() => props.cohortId);
  const reportRows = useCohortAttendanceReport(() => props.cohortId);
  const alertRows = useCohortAlerts(() => props.cohortId);
  // D3 (plan 06 · E3): the three queries lost their `leftJoin(user, …)`.
  // A single identity source for all three, instead of three identical joins.
  const people = useCohortIdentities(() => props.cohortId);
  const roster = createMemo(() =>
    attachIdentity(enrollments.data ?? [], people.identities(), (r) => r.user_id),
  );
  const report = createMemo(() =>
    sortByIdentityName(
      attachIdentity(reportRows.data ?? [], people.identities(), (r) => r.user_id),
      (r) => r.user_id ?? "",
    ),
  );
  const alerts = createMemo(() =>
    attachIdentity(alertRows.data ?? [], people.identities(), (r) => r.user_id),
  );

  const [sessionId, setSessionId] = createSignal<string | undefined>(undefined);
  const [code, setCode] = createSignal<string | null>(null);
  // F4-B: date range for the attendance export (io.attendance).
  const [fromDate, setFromDate] = createSignal("");
  const [toDate, setToDate] = createSignal("");

  const attendance = useSessionAttendance(() => sessionId());

  const orderedSessions = createMemo(() =>
    (sessions.data ?? []).toSorted((a, b) => (b.date ?? "").localeCompare(a.date ?? "")),
  );
  const activeStudents = createMemo(() => roster().filter((r) => r.status === "active"));
  const recordFor = (userId: string | null) =>
    (attendance.data ?? []).find((a) => a.user_id === userId);
  const statFor = (userId: string | null) => report().find((r) => r.user_id === userId);

  const mark = (userId: string | null, status: AttendanceStatus) => {
    const db = drizzle();
    const sid = sessionId();
    if (!db || !sid || !userId) {
      return;
    }
    const existing = recordFor(userId);
    void setAttendanceMutation(
      db,
      {
        sessionId: sid,
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        campusId: props.campusId,
      },
      { userId, status, existingId: existing?.id },
    ).catch((err: unknown) => {
      console.error("Error setting attendance:", err);
    });
  };

  const openCode = async () => {
    const sid = sessionId();
    if (!sid) {
      return;
    }
    try {
      const res = await handleApiResponse(
        api.POST("/api/class-sessions/{id}/checkin-code", {
          params: { path: { id: sid } },
        }),
      );
      setCode(res?.code ?? null);
    } catch {
      showToast(t(msg`Could not open check-in`), "error");
    }
  };

  const closeCode = async () => {
    const sid = sessionId();
    if (!sid) {
      return;
    }
    try {
      await handleApiResponse(
        api.DELETE("/api/class-sessions/{id}/checkin-code", {
          params: { path: { id: sid } },
        }),
      );
      setCode(null);
    } catch {
      showToast(t(msg`Could not close check-in`), "error");
    }
  };

  const acknowledge = async (alertId: string) => {
    try {
      await handleApiResponse(
        api.POST("/api/student-alerts/{id}/acknowledge", {
          params: { path: { id: alertId } },
        }),
      );
      showToast(t(msg`Alert acknowledged`), "success");
    } catch {
      showToast(t(msg`Could not acknowledge`), "error");
    }
  };

  return (
    <div class="space-y-5" data-testid="cohort-attendance">
      {/* F4-B: attendance export with date range (io.attendance) */}
      <div class="flex flex-wrap items-end justify-end gap-2">
        <label class="text-xs text-muted-foreground">
          {t(msg`From`)}
          <input
            type="date"
            data-testid="attendance-export-from"
            class="ms-1 rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
            value={fromDate()}
            onChange={(e) => setFromDate(e.currentTarget.value)}
          />
        </label>
        <label class="text-xs text-muted-foreground">
          {t(msg`To`)}
          <input
            type="date"
            data-testid="attendance-export-to"
            class="ms-1 rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
            value={toDate()}
            onChange={(e) => setToDate(e.currentTarget.value)}
          />
        </label>
        <ExportButton
          entityKey="io.attendance"
          orgId={props.organizationId}
          filters={{
            cohort_id: props.cohortId,
            desde: fromDate() || undefined,
            hasta: toDate() || undefined,
          }}
        />
      </div>

      {/* Risk alerts */}
      <Show when={alerts().length > 0}>
        <section class="space-y-2 rounded-2xl border-2 border-danger/40 bg-danger/5 p-4">
          <h3 class="flex items-center gap-2 text-sm font-bold text-danger">
            <BellRing class="h-4 w-4" /> {t(msg`Risk alerts`)}
          </h3>
          <For each={alerts()}>
            {(a) => (
              <div
                class="flex items-center justify-between rounded-lg bg-surface px-3 py-2 text-sm"
                data-testid="alert-row"
              >
                <span>
                  <strong>{a.name ?? a.email ?? a.user_id}</strong> · {a.kind}
                </span>
                <button
                  type="button"
                  data-testid="alert-acknowledge"
                  onClick={() => void acknowledge(a.id)}
                  class="text-xs font-bold text-primary hover:underline"
                >
                  {t(msg`Acknowledge`)}
                </button>
              </div>
            )}
          </For>
        </section>
      </Show>

      {/* Session selector */}
      <div class="flex flex-wrap items-center gap-2">
        <select
          class="rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm"
          data-testid="attendance-session-select"
          value={sessionId() ?? ""}
          onChange={(e) => {
            setSessionId(e.currentTarget.value === "" ? undefined : e.currentTarget.value);
            setCode(null);
          }}
        >
          <option value="">{t(msg`Select a session…`)}</option>
          <For each={orderedSessions()}>
            {(s) => (
              <option value={s.id}>
                {s.date} · {(s.start_time ?? "").slice(0, 5)}
              </option>
            )}
          </For>
        </select>
        <Show when={sessionId()}>
          <Button
            variant="outline"
            data-testid="open-checkin"
            onClick={() => void openCode()}
            class="gap-1"
          >
            <KeyRound class="h-4 w-4" /> {t(msg`Open check-in`)}
          </Button>
        </Show>
      </div>

      {/* Check-in code (large display) */}
      <Show when={code()}>
        <div
          class="flex flex-col items-center gap-2 rounded-2xl bg-accent/10 p-6"
          data-testid="checkin-code-display"
        >
          <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Check-in code`)}
          </p>
          <p class="font-mono text-5xl font-extrabold tracking-[0.3em] text-accent">{code()}</p>
          <button
            type="button"
            data-testid="close-checkin"
            onClick={() => void closeCode()}
            class="text-xs text-muted-foreground hover:text-danger"
          >
            {t(msg`Close check-in`)}
          </button>
        </div>
      </Show>

      {/* Pase de lista */}
      <Show
        when={sessionId()}
        fallback={
          <p class="text-sm text-muted-foreground">{t(msg`Select a session to take the roll.`)}</p>
        }
      >
        <Show
          when={activeStudents().length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No active students.`)}</p>}
        >
          <ul class="space-y-2" data-testid="roll-list">
            <For each={activeStudents()}>
              {(r) => {
                const current = () => recordFor(r.user_id)?.status ?? null;
                const stat = () => statFor(r.user_id);
                return (
                  <li
                    class="flex flex-col gap-2 rounded-xl border border-border p-3 md:flex-row md:items-center md:justify-between"
                    data-testid="roll-row"
                  >
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold">{r.name ?? r.email ?? r.user_id}</p>
                      <Show when={stat()}>
                        {(s) => (
                          <p class="text-xs text-muted-foreground" data-testid="roll-stats">
                            {t(msg`Present`)} {s().present ?? 0} · {t(msg`Absent`)}{" "}
                            {s().absent ?? 0}
                            <Show when={(s().absence_streak ?? 0) > 0}>
                              {" "}
                              · <span class="font-bold text-danger">🔥 {s().absence_streak}</span>
                            </Show>
                          </p>
                        )}
                      </Show>
                    </div>
                    <div class="flex gap-1">
                      <For each={STATUSES}>
                        {(st) => (
                          <button
                            type="button"
                            data-testid={`mark-${st.value}`}
                            aria-pressed={current() === st.value}
                            onClick={() => mark(r.user_id, st.value)}
                            class={`rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                              current() === st.value
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted/50 text-muted-foreground hover:bg-muted"
                            }`}
                          >
                            {t(st.label)}
                          </button>
                        )}
                      </For>
                    </div>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </Show>
    </div>
  );
};
